// EventContext.js - Improved version with more robust state management
import React, { createContext, useContext, useState, useRef, useCallback } from 'react';

const EventContext = createContext();

export const EventProvider = ({ children }) => {
  // Core state
  const [activeEventId, setActiveEventId] = useState(null);
  const [activeRobotId, setActiveRobotId] = useState(null);
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [videoUrl, setVideoUrl] = useState("");
  const [timeframe, setTimeframe] = useState("11AM");
  
  // Processing flags to prevent feedback loops
  const [processingUpdate, setProcessingUpdate] = useState(false);
  const updateSourceRef = useRef(null);
  const lastUpdateTimeRef = useRef(Date.now());
  
  // Debounce mechanism to prevent too frequent updates
  const shouldProcessUpdate = () => {
    const now = Date.now();
    const timeSinceLastUpdate = now - lastUpdateTimeRef.current;
    
    // Only process updates if more than 50ms have passed since last update
    if (timeSinceLastUpdate < 50) {
      return false;
    }
    
    lastUpdateTimeRef.current = now;
    return true;
  };

  // Unified state update function with source tracking
// 在EventContext.js中修改updateVideoState函数

const updateVideoState = useCallback((newState, source = null) => {
  // 最重要的改动：检测视频来源或机器人ID变化
  const isChangingVideo = 
    (newState.videoUrl !== undefined && newState.videoUrl !== videoUrl) || 
    (newState.robotId !== undefined && newState.robotId !== activeRobotId);
  
  // 如果正在切换视频，强制暂停，并且添加延迟防止播放/暂停冲突
  if (isChangingVideo) {
    console.log(`Context: 检测到视频切换，强制暂停播放`);
    
    // 立即设置暂停状态
    setIsPlaying(false);
    
    // 记录处理中状态
    setProcessingUpdate(true);
    updateSourceRef.current = source;
    
    // 更新除播放状态外的所有状态
    if (newState.eventId !== undefined) setActiveEventId(newState.eventId);
    if (newState.robotId !== undefined) setActiveRobotId(newState.robotId);
    if (newState.time !== undefined) setCurrentTime(newState.time);
    if (newState.videoUrl !== undefined) {
      setTimeout(() => {
        setVideoUrl(newState.videoUrl);
      }, 100); // 100ms 延迟设置 videoUrl
    }
    
    if (newState.timeframe !== undefined) setTimeframe(newState.timeframe);
    
    // 添加延迟，确保视频有时间停止播放并加载新内容
    setTimeout(() => {
      // 结束处理状态
      setProcessingUpdate(false);
      updateSourceRef.current = null;
      
      // 这时如果明确要求播放，才设置为播放状态（通常不会，因为我们已强制设为false）
      if (newState.isPlaying === true) {
        console.log(`Context: 视频切换后恢复播放`);
        setTimeout(() => {
          setIsPlaying(true);
        }, 500); // 再添加500ms延迟，确保视频已加载
      }
    }, 300);
    
    return; // 重要：提前返回，不执行后续代码
  }
  
  // 正常的状态更新（非视频切换情况）
  if (newState.isPlaying !== undefined) {
    console.log(`Context: 设置播放状态为 ${newState.isPlaying}`);
    setIsPlaying(newState.isPlaying);
  }
  
  if (!shouldProcessUpdate()) return;
  
  setProcessingUpdate(true);
  updateSourceRef.current = source;
  
  // 更新所有相关状态
  if (newState.eventId !== undefined) setActiveEventId(newState.eventId);
  if (newState.robotId !== undefined) setActiveRobotId(newState.robotId);
  if (newState.time !== undefined) setCurrentTime(newState.time);
  if (newState.videoUrl !== undefined) setVideoUrl(newState.videoUrl);
  if (newState.timeframe !== undefined) setTimeframe(newState.timeframe);
  
  // 释放处理锁
  setTimeout(() => {
    setProcessingUpdate(false);
    updateSourceRef.current = null;
  }, 50);
}, [activeRobotId, videoUrl, activeEventId, shouldProcessUpdate]);
  // Legacy function for backward compatibility
  const setActiveEvent = useCallback((eventId, robotId, time) => {
    updateVideoState({
      eventId: eventId !== undefined ? eventId : activeEventId,
      robotId: robotId !== undefined ? robotId : activeRobotId,
      time: time !== undefined ? time : currentTime
    }, 'setActiveEvent');
  }, [activeEventId, activeRobotId, currentTime, updateVideoState]);

  // Handle timeframe changes specially since they affect many components
  const changeTimeframe = useCallback((newTimeframe) => {
    // Update timeframe in context
    updateVideoState({ timeframe: newTimeframe }, 'changeTimeframe');
    
    // Also handle URL adjustments based on timeframe
    if (videoUrl) {
      const timePart = newTimeframe === "11AM" ? "day" : "night";
      const oppositePart = timePart === "day" ? "night" : "day";
      
      if (videoUrl.includes(`/${oppositePart}/`)) {
        const newUrl = videoUrl.replace(`/${oppositePart}/`, `/${timePart}/`);
        updateVideoState({ videoUrl: newUrl }, 'changeTimeframe');
      }
    }
  }, [videoUrl, updateVideoState]);

  return (
    <EventContext.Provider value={{ 
      // State
      activeEventId, 
      activeRobotId, 
      currentTime,
      isPlaying,
      videoUrl,
      timeframe,
      processingUpdate,
      updateSource: updateSourceRef.current,
      
      // Actions
      setActiveEvent,
      updateVideoState,
      changeTimeframe
    }}>
      {children}
    </EventContext.Provider>
  );
};

export const useEventContext = () => useContext(EventContext);